/* eslint-disable @typescript-eslint/no-unused-vars, capitalized-comments */
'use client';

import { observer } from 'mobx-react';
import { FC, useEffect, useRef } from 'react';
import TabContent from '~/components/TabContent';
import TabHeader from '~/components/TabHeader';
import { TabContentConfig, TabHeaderConfig, TabIds } from '~/definitions/tab';
import { getStore } from '~/mobx/AppStore';
import ElectionLoginPage from '~/components/ElectionLoginPage';
import sessionApi from '~/clientApis/sessionApi';

interface Props {
    tabHeader: TabHeaderConfig[];
    tabContent: TabContentConfig[];
    isMenuOpened: boolean;
    isLinkedCompanyActive: boolean;
}

const Home: FC<Props> = (props) => {
    return (
        props.isLinkedCompanyActive && (
            <>
                <TabHeader tabHeader={props.tabHeader} isMenuOpened={props.isMenuOpened} />
                <TabContent tabContent={props.tabContent} />
            </>
        )
    );
};

const HomeWithStore = () => {
    const { tabStore, userStore, companyStore, generalSettingsStore } = getStore();
    const tabs = tabStore.getTabs();
    const hasFetched = useRef(false);
    const isLoggedIn = userStore.getIsLoggedIn();
    const userNumber = userStore.user?.number;
    const isSuperAdmin = userStore.getIsSuperAdmin();

    useEffect(() => {
        if (!tabs || tabs.length === 0) return;

        // Select the first tab as the default active tab
        const activeTabId = tabs[0]?.id as TabIds;
        tabStore.setActiveTabId(activeTabId);

        // Fetch tab data for the active tab
        const fetchTabData = async () => {
            await tabStore.fetchTabData(activeTabId);
        };
        fetchTabData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userStore.isInitialized]);

    useEffect(() => {
        // Only fetch company list if not already fetched, user is logged in,
        // and the active tab is NOT 'admin' (since admin tab handles its own refresh).
        if (
            !hasFetched.current &&
            companyStore.companyList &&
            companyStore.companyList.length === 0 &&
            isLoggedIn &&
            tabStore.activeTabId !== 'admin'
        ) {
            const loadCompanyList = async () => {
                await companyStore.loadCompanyList();
            };
            loadCompanyList();

            hasFetched.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, tabStore.activeTabId]);

    useEffect(() => {
        if (isLoggedIn) {
            const companyList = companyStore.companyList;

            if (!companyList?.length) return;

            const userCompany = companyList.find((company) => company.number === userNumber);
            const selectedCompanyBySlagId = companyList.find((company) => company.id === companyStore.selectedSlagId);

            const fallbackCompany = isSuperAdmin ? companyList[0] : {};
            const fallbackSlagId = fallbackCompany?.id || '';

            const selectedCompany = selectedCompanyBySlagId || userCompany || fallbackCompany;
            const selectedSlagId = companyStore.selectedSlagId || selectedCompany?.id || fallbackSlagId;

            companyStore.setSelectedSlagId(selectedSlagId);
            companyStore.setSelectedCompany(selectedCompany);

            const selectedElectionId = generalSettingsStore?.selectedElectionData?.id || '';
            const updateSession = async () => {
                if (selectedElectionId) {
                    // Update session with selectedSlagId and selectedElectionId
                    await sessionApi.updateSession({
                        selectedSlagId,
                        selectedElectionId: selectedElectionId,
                    });
                }
            };
            updateSession();
        }
    }, [
        companyStore.companyList,
        userStore.user?.number,
        companyStore,
        userStore,
        isLoggedIn,
        isSuperAdmin,
        userNumber,
        generalSettingsStore?.selectedElectionData?.id,
    ]);

    const tabHeader: TabHeaderConfig[] = tabs.map((tab) => ({
        id: tab.id,
        name: tab.name,
        isActive: tab.isActive,
        isDisabled: tab.isDisabled,
        onClick: () => tab.onClick(),
    }));

    const tabContent: TabContentConfig[] = tabs.map((tab: TabContentConfig) => ({
        id: tab.id,
        name: tab.name,
        content: tab.content,
    }));

    const isLinkedCompanyActive = Object.keys(companyStore.selectedCompany || {}).length > 0;

    if (!isLoggedIn) {
        return <ElectionLoginPage />;
    }

    return (
        <Home
            tabHeader={tabHeader}
            tabContent={tabContent}
            isMenuOpened={tabStore.isMenuOpened}
            isLinkedCompanyActive={isLinkedCompanyActive}
        />
    );
};

export default observer(HomeWithStore);
